import { Link } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";

import { invokeEdgeFunction } from "@/lib/edge-functions";

type ExpertFull = {
  id: string;
  status: string;
  audio_submitted_at: string | null;
  writeup_ready_at: string | null;
  writeup_markdown?: string | null;
};

type Payload = {
  order: {
    id: string;
    created_at: string;
    customer_email: string;
    line_summary: string;
    status: string;
    fulfillment_note: string | null;
  };
  expert: ExpertFull;
  draftMarkdown: string;
  draftUpdatedAt: string | null;
  audioListenUrl: string | null;
};

export function AdminExpertWorkspace({ orderId }: { orderId: string }) {
  const [payload, setPayload] = useState<Payload | null>(null);
  const [markdown, setMarkdown] = useState("");
  const [loadErr, setLoadErr] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [pubErr, setPubErr] = useState("");
  const [publishing, setPublishing] = useState(false);
  const lastSent = useRef<string>("");

  const load = useCallback(async () => {
    setLoadErr("");
    try {
      const data = await invokeEdgeFunction<Payload>("admin-expert-get", { orderId });
      setPayload(data);
      const startText =
        data.draftMarkdown ||
        (typeof data.expert.writeup_markdown === "string" ? data.expert.writeup_markdown : "") ||
        "";
      lastSent.current = startText;
      setMarkdown(startText);
    } catch (e) {
      setLoadErr(e instanceof Error ? e.message : "Could not load");
      setPayload(null);
    }
  }, [orderId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (!payload || markdown === lastSent.current) return;
      setSaveState("saving");
      void invokeEdgeFunction("admin-expert-draft", { orderId, markdown })
        .then(() => {
          lastSent.current = markdown;
          setSaveState("saved");
          window.setTimeout(() => setSaveState("idle"), 1200);
        })
        .catch(() => setSaveState("idle"));
    }, 2000);
    return () => window.clearTimeout(t);
  }, [markdown, orderId, payload]);

  const publish = async () => {
    setPubErr("");
    setPublishing(true);
    try {
      await invokeEdgeFunction("admin-expert-publish", { orderId, markdown });
      lastSent.current = markdown;
      await load();
    } catch (e) {
      setPubErr(e instanceof Error ? e.message : "Network error");
    } finally {
      setPublishing(false);
    }
  };

  if (loadErr) {
    return (
      <div>
        <p className="text-sm text-rose-600">{loadErr}</p>
        <Link to="/admin/orders" className="mt-4 inline-block text-sm font-bold text-violet-700 hover:underline">
          ← Back to orders
        </Link>
      </div>
    );
  }

  if (!payload) {
    return <p className="text-sm text-zinc-500">Loading workspace…</p>;
  }

  const { order, expert, audioListenUrl } = payload;

  return (
    <div className="space-y-8">
      <Link to="/admin/orders" className="text-sm font-bold text-violet-700 underline-offset-2 hover:underline">
        ← Orders
      </Link>

      <header>
        <h1 className="text-2xl font-black tracking-tight text-zinc-900">Expert workspace</h1>
        <p className="mt-2 text-sm text-zinc-600">{order.line_summary}</p>
        <p className="mt-1 text-xs text-zinc-500">{order.customer_email}</p>
        <p className="mt-2 text-xs font-bold uppercase tracking-wide text-zinc-500">
          Voice pipeline: <span className="text-zinc-900">{expert.status}</span> · Order:{" "}
          <span className="text-zinc-900">{order.status}</span>
        </p>
      </header>

      <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-500">Customer voice memo</h2>
        {!audioListenUrl ? (
          <p className="mt-3 text-sm text-zinc-600">No audio submitted yet.</p>
        ) : (
          <audio className="mt-4 w-full" controls src={audioListenUrl}>
            <track kind="captions" />
          </audio>
        )}
      </section>

      <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-500">Writeup (Markdown)</h2>
          <span className="text-[11px] font-semibold text-zinc-500">
            {saveState === "saving" ? "Saving draft…" : saveState === "saved" ? "Draft saved" : ""}
          </span>
        </div>
        <p className="mt-2 text-xs text-zinc-500">
          Autosaves every ~2s. Publishing generates a PDF and marks the customer order fulfilled with writeup ready.
        </p>
        <textarea
          value={markdown}
          onChange={(e) => setMarkdown(e.target.value)}
          rows={18}
          className="mt-4 w-full resize-y rounded-xl border border-zinc-200 bg-zinc-50/80 px-3 py-2.5 font-mono text-sm text-zinc-900 outline-none ring-violet-400 focus:ring-2"
          placeholder="Type or paste the audit here (Markdown supported)."
        />

        {pubErr ? <p className="mt-2 text-sm text-rose-600">{pubErr}</p> : null}

        <button
          type="button"
          disabled={publishing || expert.status === "awaiting_audio"}
          onClick={() => void publish()}
          className="mt-4 rounded-xl bg-violet-600 px-5 py-3 text-sm font-bold text-white shadow hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {expert.status === "awaiting_audio"
            ? "Waiting on customer audio"
            : publishing
              ? "Publishing…"
              : "Publish writeup + PDF"}
        </button>
      </section>
    </div>
  );
}
